// state/chat.ts — Zustand store for chat threads (D7).
// Addendum 2026-04-22: the 'chiedi' thread persists to localStorage so a refresh
// doesn't wipe a student's conversation with Il Prof. Other threads (allena, simula
// review) are intentionally ephemeral — their "questions" are randomized per session
// and persisting half of one would be confusing.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AssistantPersistenceOutcome, Message, Citation } from '../types/domain';
import type { ChiediTopicState } from '../api/tutor';
import {
  parseProfFigureSnapshot,
  parsePendingProfFigure,
  type PendingProfFigure,
  type ProfFigureEvent,
  type ProfFigureSnapshot,
} from '../types/prof-figure';

const MAX_PERSISTED_PROF_TOPIC_STATES = 20;

interface ChatThreadState {
  messages: Message[];
  streaming: boolean;
}

/** Item B — a SCOPED chat thread id (`<surface>:<topicId>:<visitNonce>`) for the
 *  in-lesson / in-sub-argomento Prof. These are NOT persisted to localStorage
 *  (single-session-per-visit). The global Prof thread id is the bare 'chiedi'. */
export function isScopedThreadId(threadId: string): boolean {
  return threadId.startsWith('lesson:') || threadId.startsWith('appendice-');
}

interface ExpandedPrinciple {
  messageId: string;
  principleId: string;
}

interface ChatState {
  threads: Record<string, ChatThreadState>;
  expandedPrinciple: ExpandedPrinciple | null;
  // W3 Phase B: BE-issued session id for the active Chiedi thread.
  // - null while a fresh thread is empty (first message creates it server-side)
  // - uuid string after the BE emits the `session` SSE event on the first turn
  // - cleared back to null on clearThread('chiedi') ("Nuova conversazione")
  // The FE sends this back on every Chiedi request so the BE persists turns
  // into the same chiedi_messages thread. Defense-in-depth: localStorage
  // Chiedi persistence stays untouched, so an offline/transient FE without
  // the id still keeps a local copy of the conversation.
  chiediSessionId: string | null;

  // R-11: bounded topic continuity keyed by the BE session id. Keeping the
  // snapshots per session prevents one resumed historical conversation from
  // inheriting another thread's topic while still surviving refresh/resume.
  chiediTopicStates: Record<string, ChiediTopicState>;
  profFiguresBySession: Record<string, ProfFigureSnapshot>;
  pendingProfFiguresByThread: Record<string, PendingProfFigure>;
  profFigureNoticesByThread: Record<string, string>;
  profFigureImageStatusBySession: Record<string, ProfFigureEvent['imageStatus']>;
  setProfFigureSnapshot: (sessionId: string, snapshot: ProfFigureSnapshot, imageStatus?: ProfFigureEvent['imageStatus']) => void;
  setPendingProfFigure: (threadId: string, pending: PendingProfFigure | null) => void;
  setProfFigureNotice: (threadId: string, notice: string | null) => void;
  clearProfFigures: () => void;

  // Item B (2026-06-11) — per-VISIT scoped-thread BE session ids. The in-lesson /
  // in-sub-argomento Prof chat is a SEPARATE conversation from the global
  // 'chiedi' thread, but the founder's contract now requires it to (a) keep the
  // SAME BE session for the whole visit ("as long as I am in the same lesson, it
  // keeps going") and (b) be STORED IN CRONOLOGIA on leave (the BE persists a
  // mode='chiedi' session keyed by this id). So each scoped thread id
  // (`<surface>:<topicId>:<visitNonce>`) carries its OWN BE session id here,
  // entirely separate from the global `chiediSessionId`. A fresh visit mints a
  // new threadId (new nonce) → a fresh entry → a fresh BE session. NOT persisted
  // to localStorage (a scoped visit is single-session by design — see partialize).
  scopedSessionIds: Record<string, string>;

  // FP3a: the follow-up / continuation chips for the active Chiedi thread's
  // LAST assistant turn. Previously held only in ProfScreen React state, so a
  // refresh/resume wiped them (audit finding — chips vanish on refresh). Now
  // persisted alongside the thread so a refresh restores the same chips. Set
  // on each successful answer / refusal-with-suggestions; cleared on a new
  // send, "Nuova conversazione" (clearThread('chiedi')), and on resuming a
  // historical session (no chips are known for old sessions).
  lastSuggestions: string[] | null;

  startThread: (threadId: string) => void;
  appendMessage: (threadId: string, msg: Message) => void;
  appendToLastMessage: (threadId: string, chunk: string) => void;
  // Overwrites the text of the last message if it's text/streaming kind. Used on replace_text.
  replaceLastMessageText: (threadId: string, text: string) => void;
  // Overwrites a specific Prof text/streaming message by id.
  replaceMessageText: (threadId: string, messageId: string, text: string) => void;
  setMessageFeedbackEligible: (threadId: string, messageId: string, eligible: boolean) => void;
  setMessageAssistantPersistenceOutcome: (
    threadId: string,
    messageId: string,
    outcome: AssistantPersistenceOutcome,
  ) => void;
  setMessageCheckpointSessionId: (threadId: string, messageId: string, sessionId: string) => void;
  setStreaming: (threadId: string, value: boolean) => void;
  setCitationsOnLast: (threadId: string, cites: Citation[]) => void;
  // Converts the trailing streaming-kind message into a text message.
  // If the streaming message has empty text, removes it entirely.
  // Call this on SSE done/refused/error to clean up the "Il Prof sta scrivendo…" placeholder.
  finalizeStreamingMessage: (threadId: string) => void;
  // Removes the trailing streaming-kind placeholder regardless of text, used when a refusal
  // is surfaced through a separate message so the placeholder doesn't linger.
  removeStreamingPlaceholder: (threadId: string) => void;
  // Wipes all messages in a thread and marks it not-streaming. Used by "Nuova conversazione".
  // For threadId='chiedi' this also clears chiediSessionId — the next message creates a
  // fresh server-side thread.
  clearThread: (threadId: string) => void;
  // W3 Phase B: persist the BE-issued Chiedi session id. Called from the SSE
  // consumer when the `session` event arrives (before any tokens). Pass null
  // to wipe (only used internally by clearThread('chiedi'); no other call site).
  setChiediSessionId: (id: string | null) => void;
  setChiediTopicState: (sessionId: string, topicState: ChiediTopicState | null) => void;
  // Item B: set / clear the per-thread BE session id for a SCOPED chat thread
  // (lesson / sub-argomento). Distinct from `chiediSessionId` (the global Prof).
  setScopedSessionId: (threadId: string, id: string | null) => void;
  // Item B: drop a scoped thread's local messages + session id when the student
  // LEAVES the visit (the conversation already lives in the BE / Cronologia, and
  // returning mints a fresh thread). Keeps localStorage from growing unbounded.
  clearScopedThread: (threadId: string) => void;
  // FP3a: set the active thread's last-turn follow-up chips (persisted). Pass
  // null to clear them.
  setLastSuggestions: (suggestions: string[] | null) => void;
  expandPrinciple: (messageId: string, principleId: string) => void;
  collapsePrinciple: () => void;
}

export const useChat = create<ChatState>()(persist((set) => ({
  threads: {},
  expandedPrinciple: null,
  chiediSessionId: null,
  chiediTopicStates: {},
  profFiguresBySession: {},
  pendingProfFiguresByThread: {},
  profFigureNoticesByThread: {},
  profFigureImageStatusBySession: {},
  scopedSessionIds: {},
  lastSuggestions: null,

  startThread: (threadId) =>
    set((s) => ({
      threads: { ...s.threads, [threadId]: { messages: [], streaming: false } },
    })),

  appendMessage: (threadId, msg) =>
    set((s) => {
      const t = s.threads[threadId] ?? { messages: [], streaming: false };
      return {
        threads: {
          ...s.threads,
          [threadId]: { ...t, messages: [...t.messages, msg] },
        },
      };
    }),

  appendToLastMessage: (threadId, chunk) =>
    set((s) => {
      const t = s.threads[threadId];
      if (!t) return s;
      const last = t.messages[t.messages.length - 1];
      // Only append to messages that have a text field (text/streaming variants)
      if (!last || !('text' in last)) return s;
      if (!('kind' in last) || last.kind !== 'streaming') return s;
      const updated: Message = { ...last, text: (last.text) + chunk } as Message;
      return {
        threads: {
          ...s.threads,
          [threadId]: { ...t, messages: [...t.messages.slice(0, -1), updated] },
        },
      };
    }),

  replaceLastMessageText: (threadId, text) =>
    set((s) => {
      const t = s.threads[threadId];
      if (!t) return s;
      const last = t.messages[t.messages.length - 1];
      if (!last || !('text' in last)) return s;
      if (last.authorKind !== 'prof') return s;
      if (!('kind' in last) || last.kind !== 'streaming') return s;
      const updated = { ...last, text } as Message;
      return {
        threads: {
          ...s.threads,
          [threadId]: { ...t, messages: [...t.messages.slice(0, -1), updated] },
        },
      };
    }),

  replaceMessageText: (threadId, messageId, text) =>
    set((s) => {
      const t = s.threads[threadId];
      if (!t) return s;
      const index = t.messages.findIndex((message) => message.id === messageId);
      if (index < 0) return s;
      const message = t.messages[index];
      if (!message || !('text' in message) || message.authorKind !== 'prof') return s;
      if (!('kind' in message) || (message.kind !== 'text' && message.kind !== 'streaming')) return s;
      const updated = { ...message, text, kind: 'text' as const } as Message;
      return {
        threads: {
          ...s.threads,
          [threadId]: {
            ...t,
            messages: [
              ...t.messages.slice(0, index),
              updated,
              ...t.messages.slice(index + 1),
            ],
          },
        },
      };
    }),

  setMessageFeedbackEligible: (threadId, messageId, eligible) =>
    set((s) => {
      const t = s.threads[threadId];
      if (!t) return s;
      const index = t.messages.findIndex((message) => message.id === messageId);
      if (index < 0) return s;
      const updated = { ...t.messages[index], feedbackEligible: eligible } as Message;
      return {
        threads: {
          ...s.threads,
          [threadId]: {
            ...t,
            messages: [...t.messages.slice(0, index), updated, ...t.messages.slice(index + 1)],
          },
        },
      };
    }),

  setMessageAssistantPersistenceOutcome: (threadId, messageId, outcome) =>
    set((s) => {
      const t = s.threads[threadId];
      if (!t) return s;
      const index = t.messages.findIndex((message) => message.id === messageId);
      if (index < 0) return s;
      const updated = { ...t.messages[index], assistantPersistenceOutcome: outcome } as Message;
      return {
        threads: {
          ...s.threads,
          [threadId]: {
            ...t,
            messages: [...t.messages.slice(0, index), updated, ...t.messages.slice(index + 1)],
          },
        },
      };
    }),

  setMessageCheckpointSessionId: (threadId, messageId, sessionId) =>
    set((s) => {
      const t = s.threads[threadId];
      if (!t) return s;
      const index = t.messages.findIndex((message) => message.id === messageId);
      if (index < 0) return s;
      const updated = { ...t.messages[index], checkpointSessionId: sessionId } as Message;
      return {
        threads: {
          ...s.threads,
          [threadId]: {
            ...t,
            messages: [...t.messages.slice(0, index), updated, ...t.messages.slice(index + 1)],
          },
        },
      };
    }),

  setStreaming: (threadId, value) =>
    set((s) => {
      const t = s.threads[threadId];
      if (!t) return s;
      return { threads: { ...s.threads, [threadId]: { ...t, streaming: value } } };
    }),

  setCitationsOnLast: (threadId, cites) =>
    set((s) => {
      const t = s.threads[threadId];
      if (!t) return s;
      const last = t.messages[t.messages.length - 1];
      if (!last) return s;
      // Only text-like prof messages get citations; question/system variants are excluded
      if (last.authorKind !== 'prof') return s;
      if ('kind' in last && last.kind !== 'text' && last.kind !== 'streaming' && last.kind !== undefined) return s;
      if ('kind' in last && last.kind === 'question') return s;
      const updated = { ...last, citations: cites } as Message;
      return {
        threads: {
          ...s.threads,
          [threadId]: { ...t, messages: [...t.messages.slice(0, -1), updated] },
        },
      };
    }),

  finalizeStreamingMessage: (threadId) =>
    set((s) => {
      const t = s.threads[threadId];
      if (!t) return s;
      const last = t.messages[t.messages.length - 1];
      if (!last || last.authorKind !== 'prof') return s;
      if (!('kind' in last) || last.kind !== 'streaming') return s;
      const text = 'text' in last ? last.text : '';
      const rest = t.messages.slice(0, -1);
      const newMessages = text
        ? [...rest, { ...last, kind: 'text' as const }]
        : rest;
      return {
        threads: { ...s.threads, [threadId]: { ...t, messages: newMessages } },
      };
    }),

  removeStreamingPlaceholder: (threadId) =>
    set((s) => {
      const t = s.threads[threadId];
      if (!t) return s;
      const last = t.messages[t.messages.length - 1];
      if (!last || last.authorKind !== 'prof') return s;
      if (!('kind' in last) || last.kind !== 'streaming') return s;
      return {
        threads: { ...s.threads, [threadId]: { ...t, messages: t.messages.slice(0, -1) } },
      };
    }),

  clearThread: (threadId) =>
    set((s) => ({
      threads: { ...s.threads, [threadId]: { messages: [], streaming: false } },
      // W3 Phase B: clearing the Chiedi thread ("Nuova conversazione") wipes
      // the BE session id too — the next message starts a fresh chat thread
      // server-side. Other thread ids (allena, simula review) don't carry a
      // session id at this layer.
      chiediSessionId: threadId === 'chiedi' ? null : s.chiediSessionId,
      // FP3a: "Nuova conversazione" on the Chiedi thread also clears the
      // persisted follow-up chips so the empty state renders without stale chips.
      lastSuggestions: threadId === 'chiedi' ? null : s.lastSuggestions,
      pendingProfFiguresByThread: omitKey(s.pendingProfFiguresByThread, threadId),
      profFigureNoticesByThread: omitKey(s.profFigureNoticesByThread, threadId),
    })),

  setChiediSessionId: (id) => set({ chiediSessionId: id }),

  setProfFigureSnapshot: (sessionId, snapshot, imageStatus) => set((s) => {
    const current = s.profFiguresBySession[sessionId];
    if (current && current.revision > snapshot.revision) return s;
    const entries = Object.entries(s.profFiguresBySession).filter(([id]) => id !== sessionId);
    entries.push([sessionId, snapshot]);
    const figures = Object.fromEntries(entries.slice(-MAX_PERSISTED_PROF_TOPIC_STATES));
    const statuses = Object.fromEntries(Object.entries(s.profFigureImageStatusBySession)
      .filter(([id]) => id in figures));
    if (imageStatus) statuses[sessionId] = imageStatus;
    else if (!current || current.revision !== snapshot.revision) delete statuses[sessionId];
    return { profFiguresBySession: figures, profFigureImageStatusBySession: statuses };
  }),

  setPendingProfFigure: (threadId, pending) => set((s) => ({
    pendingProfFiguresByThread: pending
      ? { ...s.pendingProfFiguresByThread, [threadId]: pending }
      : omitKey(s.pendingProfFiguresByThread, threadId),
  })),

  setProfFigureNotice: (threadId, notice) => set((s) => ({
    profFigureNoticesByThread: notice
      ? { ...s.profFigureNoticesByThread, [threadId]: notice }
      : omitKey(s.profFigureNoticesByThread, threadId),
  })),

  clearProfFigures: () => set({
    profFiguresBySession: {}, pendingProfFiguresByThread: {},
    profFigureNoticesByThread: {}, profFigureImageStatusBySession: {},
  }),

  setChiediTopicState: (sessionId, topicState) =>
    set((state) => {
      const entries = Object.entries(state.chiediTopicStates)
        .filter(([existingSessionId]) => existingSessionId !== sessionId);
      if (topicState) entries.push([sessionId, topicState]);
      return {
        chiediTopicStates: Object.fromEntries(
          entries.slice(-MAX_PERSISTED_PROF_TOPIC_STATES),
        ),
      };
    }),

  setScopedSessionId: (threadId, id) =>
    set((s) => {
      const next = { ...s.scopedSessionIds };
      if (id === null) delete next[threadId];
      else next[threadId] = id;
      return { scopedSessionIds: next };
    }),

  clearScopedThread: (threadId) =>
    set((s) => {
      const threads = { ...s.threads };
      delete threads[threadId];
      const scopedSessionIds = { ...s.scopedSessionIds };
      delete scopedSessionIds[threadId];
      const sessionId = s.scopedSessionIds[threadId];
      return {
        threads, scopedSessionIds,
        pendingProfFiguresByThread: omitKey(s.pendingProfFiguresByThread, threadId),
        profFigureNoticesByThread: omitKey(s.profFigureNoticesByThread, threadId),
        ...(sessionId && sessionId !== s.chiediSessionId ? {
          profFiguresBySession: omitKey(s.profFiguresBySession, sessionId),
          profFigureImageStatusBySession: omitKey(s.profFigureImageStatusBySession, sessionId),
        } : {}),
      };
    }),

  setLastSuggestions: (suggestions) => set({ lastSuggestions: suggestions }),

  expandPrinciple: (messageId, principleId) =>
    set({ expandedPrinciple: { messageId, principleId } }),

  collapsePrinciple: () => set({ expandedPrinciple: null }),
}), {
  name: 'asc_chat_v1',
  storage: createJSONStorage(() => localStorage),
  // W3 (2026-04-25): persist ALL threads so the student can navigate away
  // and back without losing context. Previously only `chiedi` persisted —
  // GATE-4 review surfaced this as a real product gap ("I see no history").
  // Streaming placeholders are stripped on rehydrate so a mid-stream refresh
  // doesn't resurrect a stuck "Il Prof sta scrivendo…" indicator.
  partialize: (state) => ({
    threads: Object.fromEntries(
      Object.entries(state.threads)
        // Item B — local thread hygiene: do NOT persist SCOPED chat threads
        // (`lesson:*` / `appendice-*:*`). A scoped visit is single-session by
        // design (it lives in the BE / Cronologia and a return mints a fresh
        // thread), so persisting it would (a) grow localStorage unbounded across
        // every lesson/sub-argomento visited and (b) risk a stale scoped thread
        // resurfacing on a return that should be fresh. Only the global 'chiedi'
        // thread (and any future non-scoped thread) is persisted.
        .filter(([k]) => !isScopedThreadId(k))
        .map(([k, t]) => [k, { messages: t.messages, streaming: false }]),
    ),
    // W3 Phase B: persist the Chiedi session id too so a refresh / device
    // resume keeps writing into the same server-side thread.
    chiediSessionId: state.chiediSessionId,
    // R-11: retain only the bounded per-session continuity map. The setter
    // enforces the 20-session ceiling before this persisted slice is written.
    chiediTopicStates: state.chiediTopicStates,
    profFiguresBySession: Object.fromEntries(Object.entries(state.profFiguresBySession)
      .filter(([id]) => id === state.chiediSessionId || !Object.values(state.scopedSessionIds).includes(id))),
    pendingProfFiguresByThread: state.pendingProfFiguresByThread.chiedi
      ? { chiedi: state.pendingProfFiguresByThread.chiedi } : {},
    // FP3a: persist the last-turn follow-up chips so a refresh / resume of the
    // SAME thread restores them (audit finding — chips vanished on refresh).
    lastSuggestions: state.lastSuggestions,
    // Item B: `scopedSessionIds` is deliberately NOT persisted — scoped visits
    // are single-session; a refresh re-enters with a fresh nonce/thread anyway.
  }),
  onRehydrateStorage: () => (state) => {
    if (!state) return;
    const cleaned: Record<string, ChatThreadState> = {};
    for (const [k, t] of Object.entries(state.threads)) {
      cleaned[k] = {
        messages: t.messages.filter((m) => !('kind' in m && m.kind === 'streaming')),
        streaming: false,
      };
    }
    state.threads = cleaned;
    state.profFiguresBySession = Object.fromEntries(Object.entries(state.profFiguresBySession ?? {})
      .flatMap(([id, value]) => {
        const snapshot = parseProfFigureSnapshot(value);
        return snapshot ? [[id, snapshot]] : [];
      }).slice(-MAX_PERSISTED_PROF_TOPIC_STATES));
    const pending = parsePendingProfFigure(state.pendingProfFiguresByThread?.chiedi);
    state.pendingProfFiguresByThread = pending ? { chiedi: pending } : {};
  },
}));

function omitKey<T>(values: Record<string, T>, key: string): Record<string, T> {
  const next = { ...values };
  delete next[key];
  return next;
}
